/**
 * Panel-level edits to a dashboard spec (ACT2-1078).
 *
 * A patch names only the panels that change. The edit tools apply it to the current spec and validate
 * the merged result exactly as they validate a complete one; the UI replays the same patch over its own
 * copy with this function (`@server/dashboard-spec-patch`). It is bundled into the SPA, so it stays pure
 * and has no imports.
 *
 * The merged spec is still checked by `validateDashboardWrite`, so this only has to refuse what it
 * cannot apply.
 */

export interface DashboardSpecLike {
  parameters: unknown[];
  panels: unknown[];
}

export interface DashboardSpecPatch {
  /**
   * Whole panel objects. A panel whose `id` already exists replaces it in place; a new `id` is appended.
   * Where a panel shows is decided by its `order` field, not by its position in this list.
   */
  panels?: Record<string, unknown>[];
  /** Ids of panels to delete. */
  removePanelIds?: string[];
  /** Replaces the whole parameter list when present — `[]` deletes every parameter. */
  parameters?: Record<string, unknown>[];
}

/**
 * What a successful dashboard edit-tool result reports about the dashboard it produced: the chat
 * `preview_dashboard` / `update_dashboard` add `title` and `panelCount`, and a patch call adds the rest
 * (`patchResultFields` in `mcp/dashboard-draft-tools.ts`). The UI reads it to replay a patch.
 */
export interface DashboardToolResultSummary {
  title?: string;
  panelCount?: number;
  /** Ids of existing panels a patch replaced, in patch order. */
  replaced?: string[];
  /** Ids of panels a patch appended, in patch order. */
  added?: string[];
  /** The panel order after a patch call — absent on a full call and on results written before it existed. */
  panelIds?: string[];
}

export type DashboardSpecPatchResult<T extends DashboardSpecLike> =
  | {
      ok: true;
      spec: T;
      /** Ids of existing panels the patch replaced, in patch order. */
      replaced: string[];
      /** Ids of panels the patch appended, in patch order. */
      added: string[];
    }
  | { ok: false; error: string };

/** Every refusal the patch and the spec-or-patch resolution can return, so the tools cannot drift. */
export const DASHBOARD_PATCH_ERRORS = {
  specAndPatch: 'Send either `spec` or `panels` / `remove_panel_ids` / `parameters`, not both',
  nothingToChange: 'Nothing to change: send `spec`, or `panels` / `remove_panel_ids` / `parameters`',
  fullSpecNeedsTitleAndWarehouse:
    '`title` and `warehouse_id` are required with a full `spec` for a dashboard that does not exist yet',
  basePanelWithoutId:
    'The current dashboard has a panel without a string id, so a patch cannot address it. Send the complete `spec` instead.',
  patchPanelWithoutId: 'Every panel in panels must have a string id',
  duplicatePanel: (id: string) => `panels lists panel "${id}" more than once`,
  upsertAndRemove: (id: string) => `Panel "${id}" is both in panels and in remove_panel_ids`,
  unknownRemovals: (unknown: string[], existing: string[]) =>
    `remove_panel_ids names panels that do not exist: ${unknown.join(', ')}. Current panel ids: ${existing.join(', ')}`,
} as const;

function panelId(panel: unknown): string | undefined {
  if (typeof panel !== 'object' || panel === null) return undefined;
  const id = (panel as Record<string, unknown>)['id'];
  return typeof id === 'string' ? id : undefined;
}

export function isEmptyDashboardSpecPatch(patch: DashboardSpecPatch): boolean {
  return (
    (patch.panels?.length ?? 0) === 0 && (patch.removePanelIds?.length ?? 0) === 0 && patch.parameters === undefined
  );
}

export function applyDashboardSpecPatch<T extends DashboardSpecLike>(
  base: T,
  patch: DashboardSpecPatch,
  options: {
    /**
     * Skip, rather than refuse, a removal of a panel that is already gone. The UI replays a patch over
     * its own copy of the dashboard, which the live draft event may already have brought up to date;
     * this makes that replay idempotent. The tools keep the strict default so the model hears about a
     * mistyped id.
     */
    ignoreMissingRemovals?: boolean;
  } = {},
): DashboardSpecPatchResult<T> {
  const baseIds = base.panels.map(panelId);
  // A panel the patch cannot name would turn a replace into an append. Full-spec saves can store such
  // panels, because spec validation does not check the id's type. Only a patch that touches panels is
  // refused: a title-, description-, warehouse- or parameters-only edit leaves every panel as it is.
  const touchesPanels = (patch.panels?.length ?? 0) > 0 || (patch.removePanelIds?.length ?? 0) > 0;
  if (touchesPanels && baseIds.some((id) => id === undefined)) {
    return { ok: false, error: DASHBOARD_PATCH_ERRORS.basePanelWithoutId };
  }
  const existingIds = new Set(baseIds.filter((id): id is string => id !== undefined));

  const removeIds = new Set(patch.removePanelIds ?? []);
  const unknownRemovals = [...removeIds].filter((id) => !existingIds.has(id));
  if (unknownRemovals.length > 0 && !options.ignoreMissingRemovals) {
    return { ok: false, error: DASHBOARD_PATCH_ERRORS.unknownRemovals(unknownRemovals, [...existingIds]) };
  }

  const upserts = new Map<string, Record<string, unknown>>();
  for (const panel of patch.panels ?? []) {
    const id = panelId(panel);
    if (id === undefined) return { ok: false, error: DASHBOARD_PATCH_ERRORS.patchPanelWithoutId };
    if (upserts.has(id)) return { ok: false, error: DASHBOARD_PATCH_ERRORS.duplicatePanel(id) };
    if (removeIds.has(id)) return { ok: false, error: DASHBOARD_PATCH_ERRORS.upsertAndRemove(id) };
    upserts.set(id, panel);
  }

  const panels: unknown[] = [];
  for (const panel of base.panels) {
    const id = panelId(panel);
    if (id === undefined) {
      panels.push(panel);
      continue;
    }
    if (removeIds.has(id)) continue;
    panels.push(upserts.get(id) ?? panel);
  }
  const replaced: string[] = [];
  const added: string[] = [];
  for (const [id, panel] of upserts) {
    if (existingIds.has(id)) {
      replaced.push(id);
    } else {
      added.push(id);
      panels.push(panel);
    }
  }

  return {
    ok: true,
    spec: { ...base, panels, parameters: patch.parameters ?? base.parameters },
    replaced,
    added,
  };
}

/** The edit-tool arguments the spec-or-patch resolution reads (the MCP tools' snake_case names). */
export interface DashboardEditCall {
  title?: string;
  description?: string;
  warehouse_id?: string;
  spec?: string;
  panels?: Record<string, unknown>[];
  remove_panel_ids?: string[];
  parameters?: Record<string, unknown>[];
}

/** The dashboard a call edits: the saved row, or the open draft. */
export interface DashboardEditBase<S extends DashboardSpecLike = DashboardSpecLike> {
  title: string;
  warehouseId: string;
  description: string | null;
  spec: S;
}

export type ResolvedDashboardEdit =
  | {
      ok: true;
      title: string;
      warehouseId: string;
      /** `undefined` keeps whatever description the target already has. */
      description: string | undefined;
      /** The complete spec as JSON — the call's own `spec`, or the base with the patch applied. */
      spec: string;
      /** Set for a patch call: what it changed, for the tool result. */
      patch: { replaced: string[]; added: string[] } | null;
    }
  | { ok: false; error: string };

/**
 * Turn an edit call into complete arguments. A full `spec` passes through, taking a missing `title` /
 * `warehouse_id` / `description` from the base; a patch is applied to the base. The tools and the UI
 * replay (`ui/src/chat/lib/dashboard-draft.ts`) both resolve a call here, so the defaults cannot drift.
 * `base` is a thunk so a complete call never resolves one; `missingBaseError` is what a patch hears
 * when there is none.
 */
export function resolveDashboardEdit(
  call: DashboardEditCall,
  base: () => DashboardEditBase | null,
  missingBaseError: string,
  /** Passed to `applyDashboardSpecPatch` — the UI replay skips removals that already happened. */
  options: { ignoreMissingRemovals?: boolean } = {},
): ResolvedDashboardEdit {
  const patch: DashboardSpecPatch = {
    panels: call.panels,
    removePanelIds: call.remove_panel_ids,
    parameters: call.parameters,
  };
  if (call.spec !== undefined) {
    if (!isEmptyDashboardSpecPatch(patch)) return { ok: false, error: DASHBOARD_PATCH_ERRORS.specAndPatch };
    const current =
      call.title === undefined || call.warehouse_id === undefined || call.description === undefined ? base() : null;
    const title = call.title ?? current?.title;
    const warehouseId = call.warehouse_id ?? current?.warehouseId;
    if (title === undefined || warehouseId === undefined) {
      return { ok: false, error: DASHBOARD_PATCH_ERRORS.fullSpecNeedsTitleAndWarehouse };
    }
    const description = call.description ?? current?.description ?? undefined;
    return { ok: true, title, warehouseId, description, spec: call.spec, patch: null };
  }
  if (
    isEmptyDashboardSpecPatch(patch) &&
    call.title === undefined &&
    call.description === undefined &&
    call.warehouse_id === undefined
  ) {
    return { ok: false, error: DASHBOARD_PATCH_ERRORS.nothingToChange };
  }

  const current = base();
  if (!current) return { ok: false, error: missingBaseError };
  const patched = applyDashboardSpecPatch(current.spec, patch, options);
  if (!patched.ok) return patched;
  return {
    ok: true,
    title: call.title ?? current.title,
    warehouseId: call.warehouse_id ?? current.warehouseId,
    description: call.description ?? current.description ?? undefined,
    spec: JSON.stringify(patched.spec),
    patch: { replaced: patched.replaced, added: patched.added },
  };
}
